import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  type: 'device' | 'report' | 'bundle';
  category?: string;
  sport?: string;
  analyticsLevel?: 'starter' | 'advanced' | 'elite' | 'professional' | 'Custom';
  bundleContents?: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    type: 'device' | 'report';
    image: string;
    description?: string;
  }>;
  bundleDiscount?: number;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  image: string;
  type: 'device' | 'report' | 'bundle';
  category?: string;
  sport?: string;
  analyticsLevel?: 'starter' | 'advanced' | 'elite' | 'professional' | 'Custom';
  description?: string;
  features?: string[];
  duration?: string;
  format?: string;
  target?: string;
  bundleContents?: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    type: 'device' | 'report';
    image: string;
    description?: string;
  }>;
  bundleDiscount?: number;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  getRecommendations: (sport?: string, level?: string) => Product[];
  getProductById: (id: string) => Product | undefined;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Mock data for devices and reports
const availableProducts: Product[] = [
  // Devices
  {
    id: 'device-2',
    name: 'Catapult Vector',
    price: 150,
    image: '/lovable-uploads/catapult-512x512-optimized.png',
    type: 'device',
    category: 'gps',
    sport: 'multi-sport',
    analyticsLevel: 'elite',
    description: 'Professional-grade GPS tracking system used by elite sports teams worldwide',
    features: ['GPS & accelerometer tracking', 'Player load monitoring', 'Sprint & acceleration data']
  },
  // Reports
  {
    id: 'report-1',
    name: 'Starter Report',
    price: 25,
    image: '/lovable-uploads/e39f3be7-c77e-489d-8abf-2dec2d3dfc4a.png',
    type: 'report',
    category: 'basic',
    sport: 'multi-sport',
    analyticsLevel: 'starter',
    description: 'Team-Level Basic Analysis',
    format: '1-page PDF',
    target: 'Coaches, team managers'
  },
  {
    id: 'report-2',
    name: 'Advanced Report',
    price: 50,
    image: '/lovable-uploads/e39f3be7-c77e-489d-8abf-2dec2d3dfc4a.png',
    type: 'report',
    category: 'video-analysis',
    sport: 'multi-sport',
    analyticsLevel: 'advanced',
    description: 'Key Players + Video Analysis',
    format: '2-3 page PDF + video file link',
    target: 'Coaches, recruiters'
  },
  {
    id: 'report-3',
    name: 'Elite Report - Team Focus',
    price: 100,
    image: '/lovable-uploads/e39f3be7-c77e-489d-8abf-2dec2d3dfc4a.png',
    type: 'report',
    category: 'tactical',
    sport: 'football',
    analyticsLevel: 'elite',
    description: 'Full Tactical Analysis',
    format: '4-6 page PDF + video breakdown',
    target: 'Technical directors, scouts, advanced coaches'
  },
  {
    id: 'report-4',
    name: 'Elite Report - Player Focus',
    price: 40,
    image: '/lovable-uploads/e39f3be7-c77e-489d-8abf-2dec2d3dfc4a.png',
    type: 'report',
    category: 'recruitment',
    sport: 'multi-sport',
    analyticsLevel: 'elite',
    description: 'Individual Player Analysis for Recruitment',
    format: '2-4 page PDF per player + individual video clips',
    target: 'Parents, scouts, agents'
  }
];

// Recommendation engine
const getSmartRecommendations = (sport?: string, level?: string): Product[] => {
  const recommendations: Product[] = [];
  
  // Sport-based recommendations
  if (sport === 'football') {
    recommendations.push(
      availableProducts.find(p => p.id === 'device-2')!, // Catapult for football
      availableProducts.find(p => p.id === 'report-3')! // Elite tactical report
    );
  } else if (sport === 'multi-sport') {
    recommendations.push(
      availableProducts.find(p => p.id === 'device-2')! // Catapult for multi-sport
    );
  }
  
  // Level-based recommendations
  if (level === 'starter') {
    recommendations.push(availableProducts.find(p => p.id === 'report-1')!);
  } else if (level === 'advanced') {
    recommendations.push(
      availableProducts.find(p => p.id === 'report-2')!,
      availableProducts.find(p => p.id === 'device-2')!
    );
  } else if (level === 'elite') {
    recommendations.push(
      availableProducts.find(p => p.id === 'device-2')!,
      availableProducts.find(p => p.id === 'report-3')!,
      availableProducts.find(p => p.id === 'report-4')!
    );
  }
  
  // Remove duplicates
  const uniqueRecommendations = recommendations.filter((product, index, self) => 
    index === self.findIndex(p => p.id === product.id)
  );
  
  return uniqueRecommendations.slice(0, 4); // Limit to 4 recommendations
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const { toast } = useToast();

  const addToCart = (product: Product, quantity: number = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      
      if (existingItem) {
        toast({
          title: "Updated cart",
          description: `${product.name} quantity updated to ${existingItem.quantity + quantity}`,
        });
        
        // Track cart update event
        if (typeof window !== 'undefined' && window.plausible) {
          window.plausible('Cart Updated', {
            props: {
              product_id: product.id,
              product_name: product.name,
              price: product.price,
              new_quantity: existingItem.quantity + quantity,
              category: product.category || 'unknown',
              type: product.type
            }
          });
        }
        
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        toast({
          title: "Added to cart",
          description: `${product.name} has been added to your cart`,
        });
        
        // Track add to cart event
        if (typeof window !== 'undefined' && window.plausible) {
          window.plausible('Add to Cart', {
            props: {
              product_id: product.id,
              product_name: product.name,
              price: product.price,
              category: product.category || 'unknown',
              type: product.type,
              analytics_level: product.analyticsLevel || 'unknown'
            }
          });
        }
        
        return [...prevItems, {
          id: product.id,
          name: product.name,
          price: product.price,
          quantity,
          image: product.image,
          type: product.type,
          category: product.category,
          sport: product.sport,
          analyticsLevel: product.analyticsLevel
        }];
      }
    });
  };

  const removeFromCart = (id: string) => {
    setCartItems(prevItems => {
      const item = prevItems.find(item => item.id === id);
      if (item) {
        toast({
          title: "Removed from cart",
          description: `${item.name} has been removed from your cart`,
        });
      }
      return prevItems.filter(item => item.id !== id);
    });
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
    } else {
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.id === id ? { ...item, quantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCartItems([]);
    toast({
      title: "Cart cleared",
      description: "All items have been removed from your cart",
    });
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const getRecommendations = (sport?: string, level?: string) => {
    return getSmartRecommendations(sport, level);
  };

  const getProductById = (id: string): Product | undefined => {
    return availableProducts.find(p => p.id === id);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemCount,
      getRecommendations,
      getProductById
    }}>
      {children}
    </CartContext.Provider>
  );
};